/**
 * v9.241 — Volume-by-muscle aggregation for the dashboard's Body
 * Volume Map card.
 *
 * Aggregates completed working sets per muscle_group over a rolling
 * window (default 28 days = ~one mesocycle). Returns a count map keyed
 * by the raw DB muscle_group value (e.g. 'chest_upper', 'back_lats',
 * 'glutes') so the consuming component can render a body heatmap with
 * region colours proportional to volume.
 *
 * ## Why sets (not tonnage) is the volume metric
 *
 * Per-set tracking is the modern hypertrophy-literature consensus for
 * volume comparison across mixed-load exercises. Tonnage (sets × reps
 * × weight) heavily biases toward big compound lifts — one set of
 * 100kg squat dwarfs 30 sets of 5kg lateral raises tonnage-wise, but
 * the lateral raises were the deliberate medial-delt hypertrophy
 * stimulus for the week. Set count treats both as "one targeted dose"
 * which matches how lifters actually think about volume balance
 * ("did I hit shoulders enough this week" → counted in sets, not kg).
 *
 * Bodyweight / cardio exercises also count cleanly under set tracking
 * (one pull-up set = one back set, no need to invent a tonnage proxy
 * for bodyweight loads).
 *
 * ## What counts as a "set"
 *
 * Only sets with `completed=true` are counted. Sets logged but not
 * marked completed (the lifter started typing, didn't tap done) are
 * draft state and don't represent actual training stimulus.
 *
 * No exclusion for warmup sets at this v1 stage — the rationale
 * matches `total-volume-card.tsx`'s comment block: warmup detection
 * is per-exercise context-dependent (e.g. 60% of working weight is
 * a warmup, but for an active recovery session it might be the
 * working weight). Surfacing the simpler "all completed sets" count
 * keeps the heatmap straightforward and matches the user's setting
 * preference (`countWarmupsInVolume`) at the page level if we want
 * to honour it later.
 *
 * ## Input shape
 *
 * Reuses the same `PortfolioInputRow` shape the dashboard already
 * fetches for the strength portfolio — no extra round-trip needed,
 * the dashboard's existing query (workout_exercises joined with
 * exercises + sets) carries everything this aggregation needs.
 */

import type { PortfolioInputRow } from "@/lib/portfolio";
import { trailingWindowSubset } from "./trailing-windows";
import {
  civilDayFormatter,
  dayKeyInZone,
} from "./civil-date";

/** Rolling window default — 4 weeks = ~one training mesocycle.
 *  Long enough to surface real programming imbalances ("you
 *  haven't trained back in a month"), short enough that training
 *  changes propagate visibly into the heatmap within a couple of
 *  sessions. Industry standard for volume-balance reporting. */
export const DEFAULT_VOLUME_WINDOW_DAYS = 28;

/** Fractional set-credit for an exercise's SECONDARY muscles (migration 0139).
 *  A completed set counts fully toward its primary muscle and half toward each
 *  secondary muscle - the standard "indirect volume = half a set" hypertrophy
 *  heuristic (a coach + second-model review signed off on 0.5 within the
 *  0.3-0.5 range). Single knob: retune here to move every volume + fatigue
 *  surface at once. Set counts become FRACTIONAL as a result; every user-facing
 *  number downstream is either a per-week RATE (already fractional-formatted) or
 *  the primary-only per-exercise drill-down, so no integer "N sets" is ever
 *  rendered as "N.5". */
export const SECONDARY_VOLUME_WEIGHT = 0.5;

/**
 * Muscle-map VOLUME windows (Lever A — Pro feature). The free tier is locked
 * to DEFAULT_MUSCLE_MAP_WINDOW (7D); Pro unlocks the rest. 7D is the default
 * AND the lead pill so it lines up with the Fatigue lens (also a 7-day view):
 * the two modes describe the same rolling week and share the "Last 7 days"
 * caption, so the whole card is one coherent "this week" read by default. The
 * window is ROLLING (trailing N days from now), so for a consistent lifter the
 * 7-day count is a full week every day - it only reads low when training was
 * genuinely sparse that week (which is accurate, not an artefact). 4W / 6M /
 * 1Y / all are the Pro zoom-out to longer trends. `days: Infinity` = all-time
 * — the dashboard fetches the full finished-workout history, so aggregating
 * over an infinite window genuinely covers everything (no extra query).
 * Display order == picker order. `caption` drives the card subtitle.
 */
export const MUSCLE_MAP_WINDOWS = trailingWindowSubset([
  "7d",
  "4w",
  "6m",
  "1y",
  "all",
]);

export type MuscleMapWindowKey = (typeof MUSCLE_MAP_WINDOWS)[number]["key"];

/** Free-tier window + the default selection. 7D so Volume defaults to the same
 *  rolling week the Fatigue lens uses (one coherent "this week" read). */
export const DEFAULT_MUSCLE_MAP_WINDOW: MuscleMapWindowKey = "7d";

/** Aggregated set count keyed by raw muscle_group DB value. Empty
 *  map when no qualifying sets exist (new user, all sets outside
 *  the window, etc.). Null/empty muscle_group values are dropped
 *  silently — a tag-less exercise can't contribute to a muscle
 *  region; it's surfaced via the existing untagged-exercise
 *  warnings on the library page instead. */
export type VolumeByMuscle = Record<string, number>;

/**
 * Compute set counts per muscle_group over the trailing
 * `windowDays` from `now`. Pure function — caller supplies the
 * input rows AND the reference timestamp so the function is
 * deterministic + testable (no Date.now() inside).
 */
export function computeVolumeByMuscle(
  rows: PortfolioInputRow[],
  options?: {
    /** Reference "now" for window calculation. Defaults to current
     *  time when omitted, but callers in SSR contexts should pass
     *  the page render timestamp explicitly so the window edge
     *  doesn't drift across re-renders / hydration. */
    now?: Date;
    /** Trailing window length in days. Defaults to 28 (4 weeks). */
    windowDays?: number;
  },
): VolumeByMuscle {
  const now = options?.now ?? new Date();
  const windowDays = options?.windowDays ?? DEFAULT_VOLUME_WINDOW_DAYS;
  const windowStartMs = now.getTime() - windowDays * 86_400_000;

  const counts: VolumeByMuscle = {};

  for (const row of rows) {
    // Window filter — workout must have STARTED within the trailing
    // window. Using started_at (not ended_at) so a workout that
    // started 3 weeks ago but spans midnight into the next day
    // still counts under its start date. Matches how the lifter
    // thinks about "yesterday's workout."
    const startedAt = row.workout?.started_at;
    if (!startedAt) continue;
    const startedMs = new Date(startedAt).getTime();
    if (Number.isNaN(startedMs)) continue;
    if (startedMs < windowStartMs) continue;
    // Also require the workout to be FINISHED — in-progress
    // sessions shouldn't contribute to the heatmap yet because
    // the lifter may abandon them or be mid-set. Matches the
    // portfolio's existing "completed workouts only" semantics.
    if (!row.workout?.ended_at) continue;

    const muscleGroup = row.exercise?.muscle_group;
    if (!muscleGroup) continue;

    // Count completed sets only. The shape supplied by the
    // dashboard's fetch nests sets under each workout_exercise
    // row; iterate + filter.
    const sets = row.sets ?? [];
    let completedSetsHere = 0;
    for (const s of sets) {
      if (s.completed) completedSetsHere += 1;
    }

    if (completedSetsHere > 0) {
      counts[muscleGroup] = (counts[muscleGroup] ?? 0) + completedSetsHere;
      // Secondary muscles get fractional credit (SECONDARY_VOLUME_WEIGHT per
      // completed set). Skip a secondary that duplicates the primary so a set
      // can't double-count the same muscle. Absent column (pre-0139 DB) ->
      // empty list -> no secondary credit.
      for (const sec of row.exercise?.secondary_muscle_groups ?? []) {
        if (!sec || sec === muscleGroup) continue;
        counts[sec] =
          (counts[sec] ?? 0) + completedSetsHere * SECONDARY_VOLUME_WEIGHT;
      }
    }
  }

  return counts;
}

/**
 * Pre-collapsed region totals for the body silhouette renderer. The
 * SVG paints body regions like "shoulders", "biceps", etc. — some
 * map 1:1 to a muscle_group (biceps → biceps), others sum multiple
 * sub-buckets into one display region (e.g. the "Quads" region
 * doesn't have an upper/lower split anatomically the way chest does).
 *
 * This shape is keyed by the body-region identifier the SVG uses,
 * NOT by raw muscle_group values. Region keys are intentionally
 * stable + lowercase + underscore-separated to match the SVG's
 * `data-region` attributes one-to-one.
 *
 * Computed via `volumeByRegion()` below — wraps `computeVolumeByMuscle`
 * + applies the muscle→region mapping table.
 */
export type VolumeByRegion = Record<string, number>;

/**
 * Body-region identifiers the silhouette SVG knows how to paint.
 * These names match `data-region` attributes on the SVG paths. Each
 * region is what the SVG renders as ONE coloured area — finer
 * granularity (e.g. clavicular vs sternal pec fibres) collapses
 * into a single region here because the SVG silhouette can't
 * meaningfully separate them at iPhone-SE-card scale.
 *
 * Front view regions: chest, shoulders, biceps, forearms, core,
 * quads (front of thigh)
 *
 * Back view regions: upper_back (traps + rhomboids), lats,
 * lower_back, triceps, glutes, hamstrings, calves
 *
 * v1 fold-ins (subject to refinement once we have anatomical
 * accuracy data):
 *   - Forearms appear on both views; coloured the same on both.
 *   - Shoulders are a single region on the front view (front + side
 *     delts collapsed). The rear delt would conceptually belong on
 *     the back view but we don't currently distinguish front/side/
 *     rear delts in the muscle_group taxonomy — all delts roll up
 *     to 'shoulders'.
 *   - Chest sub-buckets (chest_upper / chest / chest_lower) at the
 *     muscle_group level all roll up to a single 'chest' region
 *     here. The SVG asset has only one Pectoralis major path —
 *     splitting BodyRegions when the SVG can't visually
 *     distinguish them caused user-trained chest_upper / chest_lower
 *     volume to silently disappear from the heatmap (paint pass
 *     looked up "chest_mid" only). Sub-bucket granularity stays
 *     alive at the muscle_group level — filter pills + library
 *     navigation + exercise card chips still read "Upper / Mid /
 *     Lower Chest." Re-expand when/if a future SVG gains separate
 *     clavicular/sternal/costal pec paths.
 *   - 'Legs' (leftover full-chain DB value: Olympic lifts, sled
 *     work) divided evenly across quads + hamstrings + glutes +
 *     calves so the volume isn't lost. Conservative split —
 *     reality varies per exercise but the divided count is more
 *     honest than dropping the rows entirely.
 *   - 'Cardio' isn't a body region; cardio set counts are returned
 *     in a separate `cardio` key the consuming card can render
 *     as a small caption ("12 cardio sets") rather than tinting
 *     a region.
 *   - 'Other' rows likewise don't map to a body region; surfaced
 *     as a count caption.
 */
export const BODY_REGIONS = [
  // v9.266 — chest sub-buckets collapsed to a single "chest" region.
  // See docblock above ("Chest sub-buckets") for rationale.
  "chest",
  "upper_back",
  "lats",
  "lower_back",
  // v9.270 — shoulders split into front (anterior, push) and rear (posterior,
  // pull) delts (migration 0121). SIDE delts added later (migration 0143): the
  // lateral head (abduction - raises, upright rows) is its own region so a
  // side-raise lifter isn't credited to front delts.
  //
  // The SVG has no separate lateral path (front.svg carries exactly two delt
  // shapes, "Deltoids (front)" and "Deltoids (rear)"), so front_delts and
  // side_delts SHARE the front figure's Deltoid path visually. NOTE the sharing
  // is NOT in muscle-label-map.ts, which this comment used to claim - that map
  // sends the path to front_delts alone, and for a while nothing rendered
  // side_delts at all: it was graded, named in the dashboard caption, and
  // invisible on the body. The sharing lives in volume-map-card.tsx
  // (resolveSharedShoulderHead), which picks whichever head drives the colour
  // per mode and routes the tap to the same one. They're still graded
  // separately everywhere else.
  "front_delts",
  "side_delts",
  "rear_delts",
  // v9.269 — neck promoted from the 'other' bucket to its own region
  // (coach taxonomy review). The SVG already has neck paths
  // (sternocleidomastoid / omohyoid) — see muscle-label-map.ts.
  "neck",
  "biceps",
  "triceps",
  "forearms",
  "core",
  // v9.270 — obliques split out of core (coach taxonomy review). The
  // SVG has a separate External obliques path; rotational / side-bend
  // work paints here, crunches/planks stay on "core" (rectus abdominis).
  "obliques",
  "quads",
  // v9.269 — adductors split out of quads/hamstrings into their own
  // region (coach taxonomy review). The SVG has dedicated adductor
  // paths front + back. NOTE: the 'legs' leftover quartering below
  // intentionally stays 4-way (quads/hams/glutes/calves) — adductors
  // only receives volume from exercises tagged 'adductors' directly.
  "adductors",
  "hamstrings",
  "glutes",
  "calves",
] as const;

export type BodyRegion = (typeof BODY_REGIONS)[number];

/** Per-region totals plus side-channel counts for non-region groups
 *  (cardio, other) that the card surfaces as captions. */
export type RegionalVolume = {
  /** Region → set count. Regions with zero sets are present in the
   *  map with value 0 so the consuming SVG can render them as the
   *  "untrained" base colour without doing its own zero-fill. */
  regions: Record<BodyRegion, number>;
  /** Number of completed cardio sets in the window — surfaced as a
   *  caption since cardio doesn't paint a body region. */
  cardioSets: number;
  /** Number of completed sets tagged 'other' / unknown in the
   *  window. Same caption treatment as cardio. */
  otherSets: number;
  /** v9.267 — per-exercise breakdown of what landed in `otherSets`.
   *  Surfaces in the dashboard's MuscleMap card as a tappable
   *  detail-list under the OTHER pill so the lifter can see WHICH
   *  exercises are falling outside the body-region taxonomy.
   *
   *  Entries cover three sources:
   *    1. Exercises tagged `muscle_group = 'other'` (intentional).
   *    2. Exercises with an unknown muscle_group string (legacy /
   *       typo / a future tag we haven't mapped yet).
   *    3. Legs / arms leftover remainder (sets that couldn't be
   *       quartered/thirded evenly across sub-regions). These get
   *       a synthetic label like "Legs (leftover split)" so the
   *       lifter understands it's a math remainder, not an
   *       uncategorised exercise. */
  otherBreakdown: OtherBreakdownEntry[];
  /** Peak region count — drives the heatmap colour scale. The most-
   *  trained region maxes out the brand-orange ramp; everything else
   *  scales relative. Returns 0 when no sets exist in the window. */
  maxRegionSets: number;
  /** Effective training weeks in the window: weeks since the FIRST qualifying
   *  workout, min 1, capped at the window length (0 when no data). Divides the
   *  per-region counts into a sets-per-WEEK rate for LANDMARK-mode grading, so
   *  a week-1 user isn't divided by the full 4 (and all-time divides by their
   *  real span, not infinity). */
  trainingWeeks: number;
};

/** One row of the OTHER-pill breakdown — see RegionalVolume.otherBreakdown. */
export type OtherBreakdownEntry = {
  /** Exercise display name (or a synthetic label like "Legs (leftover
   *  split)" for the legacy umbrella remainder). */
  label: string;
  /** Raw muscle_group string. Synthetic remainder rows use the
   *  umbrella key ("legs" / "arms"). */
  muscleGroup: string;
  /** Completed set count contributing to otherSets from this row. */
  count: number;
};

/** Map a raw muscle_group DB value to the body region it paints in
 *  the silhouette SVG. Returns null when the value doesn't map to a
 *  paintable region (cardio, other, unknown — those are counted
 *  separately for caption display). */
export function muscleGroupToRegion(mg: string): BodyRegion | null {
  switch (mg) {
    // Chest sub-buckets (data shape from migration 0072).
    // v9.266 — all 3 sub-buckets roll up to a single "chest" region
    // since the SVG has only one Pectoralis major path. See
    // BODY_REGIONS docblock for the full rationale.
    case "chest_upper":
    case "chest":
    case "chest_lower":
      return "chest";
    // Back sub-buckets (data shape from migration 0073). Bare
    // 'back' is the Upper Back leftover; rolls into 'upper_back'.
    case "back":
      return "upper_back";
    case "back_lats":
      return "lats";
    case "back_lower":
      return "lower_back";
    // Shoulders — split into front (anterior + lateral) and rear (posterior)
    // delts (migration 0121). front paints the front figure's Deltoid path,
    // rear the back figure's (muscle-label-map.ts). Bare legacy 'shoulders'
    // (untouched user customs) defaults to front_delts so it still paints +
    // counts rather than vanishing into 'other'.
    case "front_delts":
      return "front_delts";
    case "side_delts":
      return "side_delts";
    case "rear_delts":
      return "rear_delts";
    case "shoulders":
      return "front_delts";
    // v9.269 — neck (promoted from 'other'; paints sternocleidomastoid
    // / omohyoid on the front view).
    case "neck":
      return "neck";
    // Arms — biceps front view, triceps back view.
    case "biceps":
      return "biceps";
    case "triceps":
      return "triceps";
    case "forearms":
      return "forearms";
    // Core sub-buckets (data shape from the core upper/lower split
    // migration). Like chest, all 3 roll up to a single "core" region —
    // the SVG has one rectus path pair; the upper/lower distinction lives
    // in the exercise filters + drill-down bias, not as separate regions.
    case "core_upper":
    case "core":
    case "core_lower":
      return "core";
    // v9.270 — obliques split out of core (data shape from migration
    // 0079).
    case "obliques":
      return "obliques";
    // Leg sub-buckets (data shape from migration 0071).
    case "quads":
      return "quads";
    // v9.269 — adductors split out of quads/hamstrings (data shape
    // from migration 0078).
    case "adductors":
      return "adductors";
    case "hamstrings":
      return "hamstrings";
    case "glutes":
      return "glutes";
    case "calves":
      return "calves";
    // Cardio + other don't map to regions — handled by the caller.
    case "cardio":
    case "other":
      return null;
    default:
      // Unknown values (e.g. a future migration adding new tags
      // without updating this map, or a legacy 'arms' / 'legs'
      // leftover) — return null so the caller treats them as
      // 'other' rather than crashing or vanishing.
      return null;
  }
}

/**
 * Roll the raw muscle_group counts up into body-region totals plus
 * side-channel cardio / other counts. Wraps `computeVolumeByMuscle`
 * — the dashboard page calls this directly, not the underlying
 * `computeVolumeByMuscle`, unless it needs the raw map.
 */
export function computeRegionalVolume(
  rows: PortfolioInputRow[],
  options?: {
    now?: Date;
    windowDays?: number;
    /** Timestamp (ms) of the lifter's FIRST qualifying workout ever, not just
     *  the first one inside this window. Optional so existing callers and
     *  tests keep working; when absent the in-window first workout is used,
     *  which is the old (inconsistent) behaviour. See the trainingWeeks note
     *  below for why this matters. */
    firstEverMs?: number | null;
  },
): RegionalVolume {
  const byMuscle = computeVolumeByMuscle(rows, options);

  // v9.267 — per-exercise counts (keyed by `${name}::${muscle_group}`)
  // for the OTHER-bucket breakdown. Walked in parallel with the
  // existing per-muscle aggregation so we can surface WHICH
  // exercises are landing in otherSets without doing a second
  // server round-trip from the dashboard card.
  //
  // Same window/completed-set filtering as computeVolumeByMuscle —
  // intentionally duplicated rather than extracted to keep the
  // shape obvious; if a third caller wants this granularity we can
  // factor `iterateCompletedSetsInWindow` out then.
  const now = options?.now ?? new Date();
  const windowDays = options?.windowDays ?? DEFAULT_VOLUME_WINDOW_DAYS;
  const windowStartMs = now.getTime() - windowDays * 86_400_000;
  type PerExercise = { label: string; muscleGroup: string; count: number };
  const perExercise = new Map<string, PerExercise>();
  // Earliest qualifying workout in the window → effective training span.
  let firstMs: number | null = null;
  for (const row of rows) {
    const startedAt = row.workout?.started_at;
    if (!startedAt) continue;
    const startedMs = new Date(startedAt).getTime();
    if (Number.isNaN(startedMs)) continue;
    if (startedMs < windowStartMs) continue;
    if (!row.workout?.ended_at) continue;
    const mg = row.exercise?.muscle_group;
    if (!mg) continue;
    const name = row.exercise?.name ?? "(unnamed)";
    let completed = 0;
    for (const s of row.sets ?? []) if (s.completed) completed += 1;
    if (completed <= 0) continue;
    if (firstMs === null || startedMs < firstMs) firstMs = startedMs;
    const key = `${name}::${mg}`;
    const existing = perExercise.get(key);
    if (existing) {
      existing.count += completed;
    } else {
      perExercise.set(key, { label: name, muscleGroup: mg, count: completed });
    }
  }

  // Zero-fill so the consuming SVG can iterate BODY_REGIONS and
  // get a number for every region without || 0 dance.
  const regions = Object.fromEntries(
    BODY_REGIONS.map((r) => [r, 0]),
  ) as Record<BodyRegion, number>;

  let cardioSets = 0;
  let otherSets = 0;
  // Treat the legacy 'legs' leftover bucket (Olympic lifts /
  // plyometrics / sled work that didn't split into quads/hams/
  // glutes/calves in migration 0071) as quarter-credit to each
  // of the four leg sub-regions. Conservative split — the
  // movements legitimately train all four but unevenly per
  // exercise; quartering preserves total volume without
  // claiming false precision.
  let legsLeftover = 0;
  let armsLeftover = 0;

  for (const [mg, count] of Object.entries(byMuscle)) {
    if (mg === "legs") {
      legsLeftover += count;
      continue;
    }
    if (mg === "arms") {
      armsLeftover += count;
      continue;
    }
    const region = muscleGroupToRegion(mg);
    if (region) {
      regions[region] += count;
    } else if (mg === "cardio") {
      cardioSets += count;
    } else {
      otherSets += count;
    }
  }

  if (legsLeftover > 0) {
    // Distribute 1/4 to each leg sub-region. Rounded floor on
    // each so total ≤ original (no inflation). Remainder
    // (legsLeftover % 4) lands as caption-only via otherSets.
    const perRegion = Math.floor(legsLeftover / 4);
    regions.quads += perRegion;
    regions.hamstrings += perRegion;
    regions.glutes += perRegion;
    regions.calves += perRegion;
    otherSets += legsLeftover % 4;
  }
  if (armsLeftover > 0) {
    // Same split logic for legacy 'arms' values (pre-migration
    // 0026 user customs). 1/3 each to biceps/triceps/forearms.
    const perRegion = Math.floor(armsLeftover / 3);
    regions.biceps += perRegion;
    regions.triceps += perRegion;
    regions.forearms += perRegion;
    otherSets += armsLeftover % 3;
  }

  // v9.267 — build the otherBreakdown from per-exercise counts.
  // Surface any (exercise, muscleGroup) pair where the muscle_group
  // ends up in otherSets:
  //   - Direct hit: muscleGroupToRegion returns null AND mg !== 'cardio'
  //     (covers 'other'-tagged + unknown / legacy strings).
  //   - Legs/arms leftover: only surface as a remainder note when
  //     the leftover % {4,3} != 0. Show the synthetic label so the
  //     lifter sees "this is a leftover-split remainder, not an
  //     uncategorised exercise."
  // Sort by count desc so the biggest contributor shows first.
  const otherBreakdown: OtherBreakdownEntry[] = [];
  for (const entry of perExercise.values()) {
    const mg = entry.muscleGroup;
    if (mg === "legs" || mg === "arms") continue; // surfaced below as remainder
    if (mg === "cardio") continue; // not OTHER
    const region = muscleGroupToRegion(mg);
    if (region) continue; // mapped cleanly, not OTHER
    otherBreakdown.push({
      label: entry.label,
      muscleGroup: mg,
      count: entry.count,
    });
  }
  if (legsLeftover % 4 !== 0) {
    otherBreakdown.push({
      label: "Legs (leftover split remainder)",
      muscleGroup: "legs",
      count: legsLeftover % 4,
    });
  }
  if (armsLeftover % 3 !== 0) {
    otherBreakdown.push({
      label: "Arms (leftover split remainder)",
      muscleGroup: "arms",
      count: armsLeftover % 3,
    });
  }
  otherBreakdown.sort((a, b) => b.count - a.count);

  const maxRegionSets = Math.max(0, ...Object.values(regions));

  // Effective training weeks for the LANDMARK per-week rate: weeks since the
  // first qualifying workout, min 1, capped at the nominal window length.
  // Fractional (no Math.ceil) so the per-week rate tracks the real span
  // smoothly instead of stepping down (e.g. 24/wk -> 12/wk) the moment the
  // span crosses a 7-day boundary, which would flip a muscle over->under
  // with no change in training.
  const WEEK_MS = 7 * 86_400_000;
  const nominalWeeks = Number.isFinite(windowDays)
    ? Math.max(1, windowDays / 7)
    : Number.POSITIVE_INFINITY;
  // Weeks of ACTUAL TRAINING inside this window, and the divisor for the
  // sets-per-week rate that gets graded against MEV/MAV/MRV.
  //
  // Measured from the lifter's first workout EVER (capped to the window), not
  // the first one inside it. That distinction was a real bug: with the
  // in-window anchor the divisor depended on where the window boundary
  // happened to fall relative to a training gap, so a window starting just
  // before a quiet stretch got a small divisor and an inflated rate. Widening
  // the window then swallowed the gap, the divisor grew faster than the sets,
  // and the rate FELL - the same muscle read OPTIMAL at 4W and UNDER at 6M off
  // more sets. `classifyRegion`'s doc claims this rate is "comparable across
  // the 4W / 3M / 6M windows"; with the in-window anchor that was simply false.
  //
  // TEMPORARY. Per-week bucketing (in progress) replaces this synthetic span
  // entirely - once sets are grouped into real weeks there's nothing to
  // estimate. Kept small on purpose; don't build on it.
  const spanAnchorMs = options?.firstEverMs ?? firstMs;
  const trainingWeeks =
    spanAnchorMs === null || spanAnchorMs === undefined || firstMs === null
      ? 0
      : Math.min(
          Math.max(1, (now.getTime() - spanAnchorMs) / WEEK_MS),
          nominalWeeks,
        );

  return {
    regions,
    cardioSets,
    otherSets,
    otherBreakdown,
    maxRegionSets,
    trainingWeeks,
  };
}

/** One contributing exercise in a region's drill-down (Lever C). `bias`
 *  carries the raw muscle_group so the drill-down can show a sub-bias chip
 *  (Upper/Mid/Lower) for the chest + core regions that collapse multiple
 *  sub-buckets into one region; ignored for 1:1 regions (lats, quads, ...). */
export type RegionExerciseBreakdown = {
  name: string;
  sets: number;
  bias?: string;
  /** Credited to this region via a SECONDARY muscle (0139), not the exercise's
   *  primary. `sets` is the ACTUAL sets performed (the 0.5x weighting applies to
   *  the region GRADE via computeVolumeByMuscle, not this per-exercise count);
   *  the card marks these "secondary". */
  secondary?: boolean;
};

/**
 * Per-region drill-down (Lever C): region → contributing exercises
 * (completed sets, desc) over a window. Uses the DIRECT muscle_group →
 * region map, so legacy umbrella 'legs' / 'arms' tags + cardio / other are
 * NOT itemised here (they're surfaced in the card's caption pills) — a
 * region's breakdown total can therefore sit marginally below its heatmap
 * total on legacy-tagged data. Capped at the top 12 exercises per region to
 * bound the serialized payload. Same window/completed-set filter as
 * computeRegionalVolume.
 */
export type RegionBreakdowns = Partial<
  Record<BodyRegion, RegionExerciseBreakdown[]>
>;

export function computeRegionBreakdowns(
  rows: PortfolioInputRow[],
  options?: { now?: Date; windowDays?: number },
): RegionBreakdowns {
  const now = options?.now ?? new Date();
  const windowDays = options?.windowDays ?? DEFAULT_VOLUME_WINDOW_DAYS;
  const windowStartMs = now.getTime() - windowDays * 86_400_000;

  // region → (exercise name → { set count, raw muscle_group bias, secondary })
  const byRegion = new Map<
    BodyRegion,
    Map<string, { sets: number; bias: string; secondary: boolean }>
  >();
  const credit = (
    region: BodyRegion,
    name: string,
    sets: number,
    bias: string,
    secondary: boolean,
  ) => {
    let exMap = byRegion.get(region);
    if (!exMap) {
      exMap = new Map();
      byRegion.set(region, exMap);
    }
    const existing = exMap.get(name);
    if (existing) {
      existing.sets += sets;
      // Same exercise NAME logged in this region under DIFFERENT muscle_group
      // tags across sessions (e.g. retagged chest_upper -> chest_lower). The
      // sub-bias chip is no longer well-defined, so blank it: biasLabel()
      // returns null for "" and the Upper/Mid/Lower (chest) / Whole (core)
      // chip is suppressed rather than frozen on the first-seen tag. NB: do
      // NOT use `region` as the sentinel — "chest"/"core" are themselves
      // biasLabel keys ("Mid"/"Whole"), so that would show a wrong chip on
      // exactly the two regions that have one. "" is never a real tag.
      if (existing.bias !== bias) existing.bias = "";
      // Direct (primary) work wins the flag — an exercise that hits a region
      // both directly and (via a same-named row) indirectly reads as direct.
      if (!secondary) existing.secondary = false;
    } else {
      exMap.set(name, { sets, bias, secondary });
    }
  };
  for (const row of rows) {
    const startedAt = row.workout?.started_at;
    if (!startedAt) continue;
    const startedMs = new Date(startedAt).getTime();
    if (Number.isNaN(startedMs)) continue;
    if (startedMs < windowStartMs) continue;
    if (!row.workout?.ended_at) continue;
    const mg = row.exercise?.muscle_group;
    if (!mg) continue;
    let completed = 0;
    for (const s of row.sets ?? []) if (s.completed) completed += 1;
    if (completed <= 0) continue;
    const name = row.exercise?.name ?? "(unnamed)";
    // Primary (legs/arms/cardio/other map to null and aren't itemised). Then
    // each secondary region gets the exercise too, MARKED secondary. The
    // drill-down shows the ACTUAL sets performed (not the 0.5x weighted figure)
    // - "Leg Press · 3 sets · secondary" reads far cleaner to a lifter than
    // "1.5 sets"; the 0.5x weighting still applies to the region GRADE via
    // computeVolumeByMuscle, which is the number that judges over/under.
    const primaryRegion = muscleGroupToRegion(mg);
    if (primaryRegion) credit(primaryRegion, name, completed, mg, false);
    for (const s of row.exercise?.secondary_muscle_groups ?? []) {
      const region = muscleGroupToRegion(s);
      if (region) credit(region, name, completed, s, true);
    }
  }

  const result: RegionBreakdowns = {};
  for (const [region, exMap] of byRegion) {
    result[region] = Array.from(exMap, ([name, v]) => ({
      name,
      sets: v.sets,
      bias: v.bias,
      ...(v.secondary ? { secondary: true } : {}),
    }))
      .sort((a, b) => b.sets - a.sets)
      .slice(0, 12);
  }
  return result;
}

/** One civil day's regional volume. Only days with work are emitted. */
export type DailyRegionalVolume = {
  /** Civil key (YYYY-MM-DD) in the USER's zone. */
  dayKey: string;
  /** Sets per body region on this day. Regions with no work are absent. */
  regions: VolumeByRegion;
};

/**
 * Per-DAY regional volume across all history, oldest first, sparse.
 *
 * ## Why days, when the landmarks are weekly
 *
 * Because a rolling 7-day total is the honest reading of a weekly landmark,
 * and you can only roll if you have days. This replaces the calendar-week
 * buckets, which had three problems that turned out to be one problem:
 *
 *   - The newest week is always partial, so it reads artificially low. That
 *     needed a `partial` flag, hatching, a "current" label, and exclusion from
 *     every tally - four mechanisms to work around one artefact.
 *   - The grade jumped every Monday for reasons unrelated to training.
 *   - Worst: a spike STRADDLING a Monday is invisible. Eight sets on Sunday
 *     and eight on Monday is sixteen in 48 hours - precisely the overtraining
 *     the card exists to catch - and calendar weeks render it as two
 *     unremarkable weeks. A rolling total shows it as a week-long plateau
 *     above the ceiling.
 *
 * A rolling window has none of those. Every point covers a complete 7 days, so
 * there is no partial anything, no weekday to reset on, and no boundary for a
 * spike to hide across. It also matches how the SILHOUETTE has always been
 * graded (sets in the trailing window over elapsed weeks), so the two surfaces
 * finally read off the same clock.
 *
 * ## Why all history, and why sparse
 *
 * One array serves every window: the client slices it and rolls it for
 * whichever region is tapped, instead of the server shipping five overlapping
 * per-window arrays. Sparse because a lifter trains on a minority of days and
 * hits a handful of regions when they do, so this is bounded by training days
 * (a year at 4x/week is ~200 entries) rather than by calendar days.
 *
 * Dates follow lib/civil-date: convert to a civil day in the user's zone ONCE,
 * then do all arithmetic in UTC. Doing it any other way is what put React #418
 * on the heatmap twice.
 */
export function computeDailyRegionalVolume(
  rows: PortfolioInputRow[],
  options: {
    /** The lifter's zone. Bucketing in the runtime's zone instead is the
     *  hydration bug this model exists to prevent. */
    timeZone: string;
  },
): DailyRegionalVolume[] {
  const fmt = civilDayFormatter(options.timeZone);
  const byDay = new Map<string, VolumeByRegion>();

  for (const row of rows) {
    // Same qualifying rules as computeVolumeByMuscle, deliberately: workout
    // FINISHED, completed sets only, secondaries at SECONDARY_VOLUME_WEIGHT
    // and never double-counting the primary. Two aggregations that disagree
    // about what a set is would be worse than the duplication.
    const startedAt = row.workout?.started_at;
    if (!startedAt) continue;
    if (Number.isNaN(new Date(startedAt).getTime())) continue;
    if (!row.workout?.ended_at) continue;

    const muscleGroup = row.exercise?.muscle_group;
    if (!muscleGroup) continue;

    let completedSetsHere = 0;
    for (const s of row.sets ?? []) if (s.completed) completedSetsHere += 1;
    if (completedSetsHere === 0) continue;

    const dayKey = dayKeyInZone(startedAt, fmt);
    const bucket = byDay.get(dayKey) ?? {};
    const addTo = (mg: string, amount: number) => {
      const region = muscleGroupToRegion(mg);
      if (region === null) return;
      bucket[region] = (bucket[region] ?? 0) + amount;
    };
    addTo(muscleGroup, completedSetsHere);
    for (const sec of row.exercise?.secondary_muscle_groups ?? []) {
      if (!sec || sec === muscleGroup) continue;
      addTo(sec, completedSetsHere * SECONDARY_VOLUME_WEIGHT);
    }
    byDay.set(dayKey, bucket);
  }

  // No gap-filling here: gaps are the client's business when it rolls, and
  // emitting a run of empty days would defeat the point of a sparse array.
  return [...byDay.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([dayKey, regions]) => ({ dayKey, regions }));
}
